import React, {ReactNode, useRef, useState} from 'react'
import Slider from 'react-slick'

import Carousel from '@/components/carousel'
import FeatureBlock, {Props as FeatureBlockProps} from '@/components/feature-block'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {TitleLayout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import {breakpoints} from '@helios/shared/theme'

import * as s from './quote-carousel.styles'

interface Item extends Pick<FeatureBlockProps, 'image' | 'mirrored'> {
    quote: FeatureBlockProps['title']
    attribution: {
        name?: string | null
        title?: string | null
    }
}

interface Props {
    title?: string
    items: Item[]
    dataTestId?: string
}

const QuoteCarousel = ({title, items, dataTestId}: Props) => {
    const [activeSlide, setActiveSlide] = useState(0)
    const ref = useRef<Slider>(null)
    const globalStrings = useGlobalStrings()

    const dots = items.map(({quote}, i) => (
        <s.CarouselDot
            key={`${quote}-${i}`}
            $isActive={i === activeSlide}
            onClick={() => ref.current?.slickGoTo(i)}
            aria-label={`${globalStrings.panel} ${i + 1}`}
        />
    ))

    return (
        <s.SectionWrapper data-testId={dataTestId}>
            {title && (
                <s.TitleWrapper>
                    <TitleLayout>
                        <Text variant="heading-large" as="h2">
                            {title}
                        </Text>
                    </TitleLayout>
                </s.TitleWrapper>
            )}

            {items.length === 1 ? (
                <s.Wrapper>
                    <Quote {...items[0]} />
                </s.Wrapper>
            ) : (
                <Carousel
                    ref={ref}
                    beforeChange={(_, currentSlide) => setActiveSlide(currentSlide)}
                    swipe={false}
                    dots={false}
                    responsive={[
                        {
                            breakpoint: breakpoints.sm,
                            settings: {
                                swipe: true,
                            },
                        },
                    ]}
                >
                    {items.map((item, i) => (
                        <div key={`${item.quote}-${i}`}>
                            <s.Wrapper>
                                <Quote {...item}>
                                    {items.length > 1 && (
                                        <s.CarouselDots $showOn="desktop">{dots}</s.CarouselDots>
                                    )}
                                </Quote>
                            </s.Wrapper>
                        </div>
                    ))}
                </Carousel>
            )}
        </s.SectionWrapper>
    )
}

interface QuoteProps extends Item {
    children?: ReactNode
}

const Quote = ({quote, attribution: {name, title}, children, ...props}: QuoteProps) => {
    return (
        <FeatureBlock withQuotes title={quote} titleVariant="title-base" {...props}>
            {(name || title) && (
                <Text>
                    {name && <strong>{name}</strong>} {title}
                </Text>
            )}
            {children}
        </FeatureBlock>
    )
}

export default QuoteCarousel
